import { BaseSymbol } from '@ui/assets/config/trade-pair-listings';
import { getChainName } from '@ui/constants/chain';
import { filterDisabledTradePairs } from '@ui/constants/tokens/tradePairs';
import { TradePairTag } from '@ui/constants/tokens/tradePairTags';
import { MarketSchedulePerPair } from '@ui/features/RWA/hooks/usePairMarketScheduleStore/type';
import { usePairMarketScheduleStore } from '@ui/features/RWA/hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';
import { useDataChainId } from '@ui/hooks/datachain';
import {
  useTradePairInfos,
  useUniqueSortedTradePairInfos,
} from '@ui/hooks/trade/useTradePairInfos';
import { TradePair } from '@ui/types/tradePair';
import { isFresh } from '@ui/utils/isFresh';
import { useMemo } from 'react';

import { useTabsStore } from '../components/MassiveTokenSelectTable/hooks/useMassiveTokenTabsStore';
import { useTradePairSearchStore } from '../components/MassiveTokenSelectTable/hooks/useTradePairSearchStore';
import { useTradePairWatchListStore } from '../components/MassiveTokenSelectTable/hooks/useTradePairWatchListStore';
import { CategoryTag } from '../constants';

export const useTradePairsShowing = () => {
  const tradingPairs = useUniqueSortedTradePairInfos();
  const pairs = useTradePairInfos();
  const watchList = useTradePairWatchListStore((s) => s.useCurrentWatchList());
  const currentCategory = useTabsStore((s) => s.currentCategory);
  const currentSubCategory = useTabsStore((s) => s.currentSubCategory);
  const searchString = useTradePairSearchStore((s) => s.searchString);

  const chainId = useDataChainId();
  const marketSchedule = usePairMarketScheduleStore(
    (s) => s._marketSchedule[getChainName(chainId)],
  );

  return useMemo(() => {
    const filteredTradePairsByTags = filterPairByTags({
      currentCategory,
      currentSubCategory,
      tradingPairs,
      watchList,
      pairs,
    });
    const searchStringFilter = filterBySearchString({
      pairs: filteredTradePairsByTags,
      searchString,
    });
    const toppedWatchListTradePairs = putWatchListPairAtTop({
      tradingPairs: searchStringFilter,
      watchList,
      marketSchedule,
    });

    return filterDisabledTradePairs(toppedWatchListTradePairs);
  }, [
    pairs,
    currentCategory,
    currentSubCategory,
    searchString,
    tradingPairs,
    watchList,
    marketSchedule,
  ]);
};
const putWatchListPairAtTop = ({
  tradingPairs,
  watchList,
  marketSchedule,
}: {
  tradingPairs: TradePair[] | undefined;
  watchList: BaseSymbol[];
  marketSchedule: MarketSchedulePerPair | undefined;
}): TradePair[] | undefined => {
  // find pairs both in tradingPairs and watchList, and make it on top of the list
  if (!tradingPairs) return tradingPairs;

  // Separate pairs into watched and unwatched
  const watchedPairsOpen: TradePair[] = [];
  const watchedPairsClose: TradePair[] = [];
  const unwatchedPairsOpen: TradePair[] = [];
  const unwatchedPairsClose: TradePair[] = [];

  tradingPairs.forEach((pair) => {
    const isClose = marketSchedule?.[pair.pairName]?.isClientOpen === false;

    if (watchList.includes(pair.baseSymbol)) {
      if (isClose) {
        watchedPairsClose.push(pair);
      } else {
        watchedPairsOpen.push(pair);
      }
    } else {
      if (isClose) {
        unwatchedPairsClose.push(pair);
      } else {
        unwatchedPairsOpen.push(pair);
      }
    }
  });

  // Combine arrays with watched pairs first
  return [
    ...watchedPairsOpen,
    ...watchedPairsClose,
    ...unwatchedPairsOpen,
    ...unwatchedPairsClose,
  ];
};

const filterPairByTags = ({
  currentCategory,
  currentSubCategory,
  tradingPairs,
  watchList,
  pairs,
}: {
  tradingPairs: TradePair[] | undefined;
  currentCategory: CategoryTag;
  currentSubCategory: TradePairTag | null;
  watchList: BaseSymbol[];
  pairs: readonly TradePair[];
}) => {
  if (!tradingPairs) return;

  switch (currentCategory) {
    case CategoryTag.ALL:
      return tradingPairs; // Show all pairs
    case CategoryTag.FAVORITES:
      return tradingPairs.filter((pair) => watchList.includes(pair.baseSymbol));
    // case CategoryTag.NEW:
    //   return tradingPairs.filter((pair) => {
    //     const degenPair = pairs.find(
    //       // check if there is a degen pair with the same base symbol
    //       (_pair) => _pair.isDegenPair && _pair.baseSymbol === pair.baseSymbol,
    //     );
    //     return !!degenPair // cater same item in normal pairs and degenPairs, and filter to new if normal or degen is new listed
    //       ? isFresh(degenPair.listedAt) || isFresh(pair.listedAt)
    //       : isFresh(pair.listedAt);
    //   });

    // For CRYPTO / RWA category, filter by subcategory
    case CategoryTag.CRYPTO:
      return tradingPairs.filter((pair) =>
        pair.tags.includes(CategoryTag.CRYPTO),
      );
    case CategoryTag.STOCKS:
      return tradingPairs.filter((pair) =>
        pair.tags.includes(CategoryTag.STOCKS),
      );
    case CategoryTag.COMMODITIES:
      return tradingPairs.filter((pair) =>
        pair.tags.includes(CategoryTag.COMMODITIES),
      );
    case CategoryTag.FOREX:
      return tradingPairs.filter((pair) =>
        pair.tags.includes(CategoryTag.FOREX),
      );
    case CategoryTag.INDICES:
      return tradingPairs.filter((pair) =>
        pair.tags.includes(CategoryTag.INDICES),
      );
  }
};

const filterBySearchString = ({
  pairs,
  searchString,
}: {
  pairs: TradePair[] | undefined;
  searchString: string;
}) => {
  if (!pairs || !searchString) return pairs;

  return pairs.filter((pair) =>
    pair.baseSymbol.toLowerCase().includes(searchString.toLowerCase()),
  );
};
